import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getMyReviews, deleteMyReview, updateMyReview } from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Star, Calendar, User, Trash2, Pencil, X } from 'lucide-react';

const RatingBar = ({ label, value }) => (
  <div className="flex items-center gap-2">
    <span className="text-sm text-stone-500 w-28">{label}</span>
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`w-4 h-4 ${
            star <= value ? 'fill-[#D97757] text-[#D97757]' : 'fill-stone-200 text-stone-200'
          }`}
        />
      ))}
    </div>
  </div>
);

export default function MyReviewsPage() {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [editingReview, setEditingReview] = useState(null);
  const [editFormData, setEditFormData] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const data = await getMyReviews();
        setReviews(data);
      } catch (error) {
        console.error('Error fetching reviews:', error);
        toast.error('Failed to load your reviews');
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  const openEditModal = (review) => {
    setEditingReview(review);
    setEditFormData({
      service_quality_rating: review.service_quality_rating,
      punctuality_rating: review.punctuality_rating,
      payment_rating: review.payment_rating,
      communication_rating: review.communication_rating,
      service_notes: review.service_notes || '',
      general_notes: review.general_notes || '',
      is_anonymous: review.is_anonymous || false
    });
  };

  const closeEditModal = () => {
    setEditingReview(null);
    setEditFormData(null);
  };

  const handleSaveEdit = async () => {
    if (!editingReview || !editFormData) return;
    
    setSavingEdit(true);
    try {
      const updatedReview = await updateMyReview(editingReview.id, editFormData);
      setReviews(reviews.map(r => r.id === editingReview.id ? updatedReview : r));
      toast.success('Review updated successfully');
      closeEditModal();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update review';
      toast.error(message);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (deletingId) return;
    
    const confirmed = window.confirm(
      'Are you sure you want to delete this review? This action cannot be undone.'
    );
    
    if (!confirmed) return;
    
    setDeletingId(reviewId);
    try {
      await deleteMyReview(reviewId);
      setReviews(reviews.filter(r => r.id !== reviewId));
      toast.success('Review deleted successfully');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete review';
      toast.error(message);
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="my-reviews-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" size="icon" className="rounded-full" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="font-heading text-2xl font-semibold text-stone-900">My Reviews</h1>
            <p className="text-stone-600">All reviews you have written</p>
          </div>
        </div>

        {/* Reviews List */}
        <Card className="border-stone-100 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-stone-900">
              {loading ? 'Loading...' : `${reviews.length} Review${reviews.length !== 1 ? 's' : ''}`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center text-stone-500">Loading your reviews...</div>
            ) : reviews.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-stone-500 mb-4">You haven&apos;t written any reviews yet.</p>
                <Link to="/clients">
                  <Button className="h-10 px-6 rounded-full bg-[#D97757] text-white hover:bg-[#C06040]">
                    Browse Clients
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {reviews.map((review) => (
                  <div 
                    key={review.id} 
                    className="p-5 rounded-xl bg-stone-50 border border-stone-100"
                    data-testid={`review-${review.id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <Link 
                          to={`/clients/${review.client_id}`}
                          className="font-medium text-stone-900 hover:text-[#D97757] transition-colors"
                        >
                          {review.client_name || 'Unknown Client'}
                        </Link>
                        <div className="flex items-center gap-3 mt-1 text-sm text-stone-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(review.created_at)}
                          </span>
                          {review.is_anonymous && (
                            <span className="flex items-center gap-1 text-stone-400">
                              <User className="w-3 h-3" />
                              Posted anonymously
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEditModal(review)}
                          className="p-2 rounded-full text-stone-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                          title="Edit review"
                          data-testid={`edit-btn-${review.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteReview(review.id)}
                          disabled={deletingId === review.id}
                          className="p-2 rounded-full text-stone-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                          title="Delete review"
                          data-testid={`delete-btn-${review.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-white border border-stone-200">
                          <Star className="w-4 h-4 fill-[#D97757] text-[#D97757]" />
                          <span className="font-semibold text-stone-900">{review.overall_rating.toFixed(1)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <RatingBar label="Service Quality" value={review.service_quality_rating || 0} />
                      <RatingBar label="Punctuality" value={review.punctuality_rating || 0} />
                      <RatingBar label="Payment" value={review.payment_rating || 0} />
                      <RatingBar label="Communication" value={review.communication_rating || 0} />
                    </div>

                    {(review.general_notes || review.service_notes) && (
                      <div className="pt-4 border-t border-stone-200 space-y-2">
                        {review.general_notes && (
                          <p className="text-stone-700">{review.general_notes}</p>
                        )}
                        {review.service_notes && (
                          <div className="p-3 bg-stone-100 rounded-lg">
                            <p className="text-xs font-medium text-stone-500 uppercase tracking-wide mb-1">Service Details</p>
                            <p className="text-sm text-stone-600">{review.service_notes}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Review Modal */}
      {editingReview && editFormData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-stone-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-stone-900">Edit Review</h3>
              <button
                onClick={closeEditModal}
                className="p-2 rounded-full hover:bg-stone-100 transition-colors"
              >
                <X className="w-5 h-5 text-stone-500" />
              </button>
            </div>
            
            <div className="p-6 space-y-5">
              {/* Rating inputs */}
              {[
                { key: 'service_quality_rating', label: 'Service Quality' },
                { key: 'punctuality_rating', label: 'Punctuality' },
                { key: 'payment_rating', label: 'Payment' },
                { key: 'communication_rating', label: 'Communication' }
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-stone-700 mb-2">{label}</label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setEditFormData({ ...editFormData, [key]: star })}
                        className="p-1 transition-colors"
                      >
                        <Star
                          className={`w-7 h-7 ${
                            star <= editFormData[key]
                              ? 'fill-[#D97757] text-[#D97757]'
                              : 'fill-stone-200 text-stone-200'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              {/* Service Notes */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Service Notes
                </label>
                <textarea
                  value={editFormData.service_notes}
                  onChange={(e) => setEditFormData({ ...editFormData, service_notes: e.target.value })}
                  rows={3}
                  className="w-full rounded-lg border border-stone-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5F8D4E] focus:border-transparent"
                  placeholder="Details about the service provided..."
                />
              </div>

              {/* General Notes */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  General Notes
                </label>
                <textarea
                  value={editFormData.general_notes}
                  onChange={(e) => setEditFormData({ ...editFormData, general_notes: e.target.value })}
                  rows={3}
                  className="w-full rounded-lg border border-stone-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5F8D4E] focus:border-transparent"
                  placeholder="Any additional comments..."
                />
              </div>

              {/* Anonymous toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="edit-anonymous"
                  checked={editFormData.is_anonymous}
                  onChange={(e) => setEditFormData({ ...editFormData, is_anonymous: e.target.checked })}
                  className="w-4 h-4 rounded border-stone-300 text-[#5F8D4E] focus:ring-[#5F8D4E]"
                />
                <label htmlFor="edit-anonymous" className="text-sm text-stone-700">
                  Post anonymously
                </label>
              </div>
            </div>

            <div className="p-6 border-t border-stone-200 flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={closeEditModal}
                disabled={savingEdit}
                className="px-4"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEdit}
                disabled={savingEdit}
                className="px-4 bg-[#5F8D4E] hover:bg-[#4a7340] text-white"
              >
                {savingEdit ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
